/**
 * InterRaterView — E4-S1 REVIEW-section view.
 *
 * Consumes GET /analytics/inter-rater?event_id=…[&assessment_instance_id=…].
 *
 * Framing (CEO directive): calibration, not surveillance. The UI copy
 * says so plainly at the top so a cadre leader opening this in a group
 * setting cannot misread the intent.
 */

import { useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import BalanceIcon from '@mui/icons-material/Balance';
import { apiFetch } from '../providers/api-rest-provider';

interface EvaluatorDistribution {
  recorded_by: string;
  sampleSize: number;
  passRatePct: number | null;
  mean: number | null;
  stddev: number | null;
  min: number | null;
  max: number | null;
}

interface InterRaterResponse {
  event_id: string;
  assessment_instance_id: string | null;
  evaluators: EvaluatorDistribution[];
}

interface InterRaterRequestState {
  key: string;
  data: InterRaterResponse | null;
  loading: boolean;
  error: string | null;
}

const fmt = (n: number | null, suffix = '') =>
  n === null ? '—' : `${n}${suffix}`;

export function InterRaterView() {
  const [eventId, setEventId] = useState('');
  const [instanceId, setInstanceId] = useState('');
  const event = eventId.trim();
  const instance = instanceId.trim();
  const requestKey = `${event}\u0000${instance}`;
  const [request, setRequest] = useState<InterRaterRequestState | null>(null);
  const currentRequest = request?.key === requestKey ? request : null;
  const data = currentRequest?.data ?? null;
  const loading = currentRequest?.loading ?? Boolean(event);
  const error = currentRequest?.error ?? null;

  useEffect(() => {
    if (!event) return;
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setRequest({ key: requestKey, data: null, loading: true, error: null });
      try {
        const qs = new URLSearchParams({ event_id: event });
        if (instance) qs.set('assessment_instance_id', instance);
        const raw = await apiFetch(
          `analytics/inter-rater?${qs.toString()}`,
          { signal: controller.signal },
        ) as InterRaterResponse;
        if (controller.signal.aborted) return;
        if (
          raw.event_id !== event ||
          (instance
            ? raw.assessment_instance_id !== instance
            : raw.assessment_instance_id !== null)
        ) {
          throw new Error('Inter-rater response does not match the requested scope.');
        }
        setRequest({ key: requestKey, data: raw, loading: false, error: null });
      } catch (err) {
        if (controller.signal.aborted) return;
        setRequest({
          key: requestKey,
          data: null,
          loading: false,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }, 300);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [event, instance, requestKey]);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <BalanceIcon color="primary" />
        <Typography variant="h5">Inter-rater calibration</Typography>
        <Chip label="E4-S1" size="small" color="warning" />
      </Box>

      {/* CEO framing directive: calibration, NOT surveillance. */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle sx={{ fontWeight: 700 }}>Calibration, not surveillance</AlertTitle>
        This view exists so cadre leaders can see whether evaluators are converging
        on the same standard. Use it to run refresher sessions on rubric interpretation
        — not to rank raters.
      </Alert>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="event_id"
              fullWidth
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              helperText="Required. The rubric event to look at."
            />
            <TextField
              label="assessment_instance_id (optional)"
              fullWidth
              value={instanceId}
              onChange={(e) => setInstanceId(e.target.value)}
              helperText="Narrow to a single scheduled run."
            />
          </Stack>
        </CardContent>
      </Card>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {data && !loading && (
        <>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
            <Chip label={`event: ${data.event_id}`} size="small" color="primary" />
            {data.assessment_instance_id && (
              <Chip label={`instance: ${data.assessment_instance_id}`} size="small" />
            )}
            <Chip label={`${data.evaluators.length} evaluator${data.evaluators.length !== 1 ? 's' : ''}`} size="small" />
          </Stack>

          {data.evaluators.length === 0 ? (
            <Alert severity="info">
              No attempts recorded for this event yet. Once instructors submit
              attempts, per-evaluator distributions appear here.
            </Alert>
          ) : (
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Evaluator</TableCell>
                    <TableCell align="right">Samples</TableCell>
                    <TableCell align="right">Pass rate</TableCell>
                    <TableCell align="right">Mean</TableCell>
                    <TableCell align="right">Std dev</TableCell>
                    <TableCell align="right">Min</TableCell>
                    <TableCell align="right">Max</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.evaluators.map((row) => (
                    <TableRow key={row.recorded_by}>
                      <TableCell>
                        <Chip
                          label={row.recorded_by === '(unattributed)' ? '(pre-E4-S1)' : row.recorded_by}
                          size="small"
                          variant={row.recorded_by === '(unattributed)' ? 'outlined' : 'filled'}
                        />
                      </TableCell>
                      <TableCell align="right">{row.sampleSize}</TableCell>
                      <TableCell align="right">{fmt(row.passRatePct, '%')}</TableCell>
                      <TableCell align="right">{fmt(row.mean)}</TableCell>
                      <TableCell align="right">{fmt(row.stddev)}</TableCell>
                      <TableCell align="right">{fmt(row.min)}</TableCell>
                      <TableCell align="right">{fmt(row.max)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}

      {!data && !loading && !error && !event && (
        <Alert severity="info">
          Enter an <code>event_id</code> above to see the per-evaluator distributions.
        </Alert>
      )}
    </Box>
  );
}
